import { create } from 'zustand';
interface GameState {
  score: number;
  highScore: number;
  speed: number;
  isPlaying: boolean;
  isPaused: boolean;
  isGameOver: boolean;
  lives: number;
  powerUps: {
    speed: boolean;
    fly: boolean;
    shield: boolean;
  };
  powerUpTimers: {
    speed: number;
    fly: number;
    shield: number;
  };
  // Actions
  startGame: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  endGame: () => void;
  resetGame: () => void;
  incrementScore: (points: number) => void;
  increaseSpeed: () => void;
  loseLife: () => void;
  addLife: () => void;
  activatePowerUp: (type: 'speed' | 'fly' | 'shield' | 'score') => void;
  deactivatePowerUp: (type: 'speed' | 'fly' | 'shield') => void;
  updatePowerUpTimers: (delta: number) => void;
}
export const useGameStore = create<GameState>((set, get) => ({
  score: 0,
  highScore: parseInt(localStorage.getItem('highScore') || '0'),
  speed: 5,
  isPlaying: false,
  isPaused: false,
  isGameOver: false,
  lives: 3,
  powerUps: {
    speed: false,
    fly: false,
    shield: false,
  },
  powerUpTimers: {
    speed: 0,
    fly: 0,
    shield: 0,
  },
  startGame: () => set({
    isPlaying: true,
    isPaused: false,
    isGameOver: false,
    score: 0,
    speed: 5,
    lives: 3,
    powerUps: { speed: false, fly: false, shield: false },
    powerUpTimers: { speed: 0, fly: 0, shield: 0 },
  }),
  pauseGame: () => set({ isPaused: true }),
  resumeGame: () => set({ isPaused: false }),
  endGame: () => {
    const { score, highScore } = get();
    if (score > highScore) {
      localStorage.setItem('highScore', score.toString());
      set({ isGameOver: true, isPlaying: false, highScore: score });
    } else {
      set({ isGameOver: true, isPlaying: false });
    }
  },
  resetGame: () => set({
    score: 0,
    speed: 5,
    isPlaying: false,
    isPaused: false,
    isGameOver: false,
    lives: 3,
    powerUps: { speed: false, fly: false, shield: false },
    powerUpTimers: { speed: 0, fly: 0, shield: 0 },
  }),
  incrementScore: (points) => set((state) => ({
    score: state.score + points
  })),
  increaseSpeed: () => set((state) => ({
    speed: Math.min(state.speed + 0.1, 15)
  })),
  loseLife: () => {
    const { lives } = get();
    if (lives <= 1) {
      get().endGame();
    } else {
      set({ lives: lives - 1 });
    }
  },
  addLife: () => set((state) => ({
    lives: Math.min(state.lives + 1, 5)
  })),
  activatePowerUp: (type) => {
    if (type === 'score') {
      set((state) => ({
        score: state.score + 100,
        powerUps: { ...state.powerUps }
      }));
    } else {
      set((state) => ({
        powerUps: { ...state.powerUps, [type]: true },
        powerUpTimers: { ...state.powerUpTimers, [type]: 5000 }
      }));
    }
  },
  deactivatePowerUp: (type) => set((state) => ({
    powerUps: { ...state.powerUps, [type]: false },
    powerUpTimers: { ...state.powerUpTimers, [type]: 0 }
  })),
  updatePowerUpTimers: (delta) => set((state) => {
    const newTimers = { ...state.powerUpTimers };
    const newPowerUps = { ...state.powerUps };
    for (const type of ['speed', 'fly', 'shield'] as const) {
      if (newPowerUps[type]) {
        newTimers[type] -= delta * 16.67;
        if (newTimers[type] <= 0) {
          newPowerUps[type] = false;
          newTimers[type] = 0;
        }
      }
    }
    return { powerUpTimers: newTimers, powerUps: newPowerUps };
  }),
}));